<template>
  <div class="mb-3">
    <div class="table-responsive">
      <table class="table table-striped table-hover align-middle mb-0">
        <thead>
          <tr>
            <th>Evento</th>
            <th>Venda</th>
            <th>Venc.</th>
            <th>Ref. aba</th>
            <th>Autorização</th>
            <th>NSU</th>
            <th>Parcela</th>
            <th>Modalidade</th>
            <th class="text-end">Valor evento</th>
            <th>Status</th>
            <th>Obs.</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!items.length">
            <td colspan="12" class="text-center text-muted py-4">
              Nenhum ajuste da operadora encontrado.
            </td>
          </tr>

          <tr v-for="item in items" :key="item.id">
            <td>
              <div class="d-flex flex-column gap-1">
                <span class="status-badge" :class="eventoClass(item.tipo_evento)">
                  {{ eventoLabel(item.tipo_evento) }}
                </span>

                <small class="text-muted">
                  {{ item.operadora_nome || '—' }}
                </small>
              </div>
            </td>

            <td>{{ formatDate(item.data_venda) }}</td>
            <td>{{ formatDate(item.data_vencimento) }}</td>
            <td>{{ formatDate(dataReferencia(item)) }}</td>
            <td>{{ item.autorizacao || '—' }}</td>
            <td>{{ item.nsu || '—' }}</td>
            <td>{{ parcelaLabel(item) }}</td>

            <td>
              <div class="d-flex flex-column">
                <span>{{ item.modalidade || '—' }}</span>
                <small class="text-muted">{{ item.bandeira || '—' }}</small>
              </div>
            </td>

            <td class="text-end">
              {{ formatCurrency(valorPrincipal(item)) }}
            </td>

            <td>
              <span class="status-badge is-amber">
                {{ normalizeStatus(item.status_conciliacao || 'PENDENTE_AJUSTE') }}
              </span>
            </td>

            <td>
              <span :title="item.observacoes || ''">
                {{ resumirTexto(item.observacoes) }}
              </span>
            </td>

            <td class="table-action-cell">
              <i
                class="bi bi-eye-fill text-primary mx-1 action-icon"
                title="Detalhar"
                @click="$emit('detalhar', { item })"
              ></i>

              <template v-if="isPendenteAjuste(item)">
                <i
                  class="bi bi-check2-circle text-success mx-1 action-icon"
                  title="Ajustar"
                  @click="$emit('tratar', { item, acao: 'AJUSTAR' })"
                ></i>

                <i
                  class="bi bi-slash-circle text-warning mx-1 action-icon"
                  title="Ignorar"
                  @click="$emit('tratar', { item, acao: 'IGNORAR' })"
                ></i>
              </template>

              <template v-else>
                <i
                  class="bi bi-arrow-counterclockwise text-primary mx-1 action-icon"
                  title="Reabrir"
                  @click="$emit('tratar', { item, acao: 'REABRIR' })"
                ></i>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination" class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ pagination.total || 0 }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <select
          class="form-select form-select-sm"
          style="width: 6rem"
          :value="pagination.limit || 10"
          @change="emit('change-limit', Number($event.target.value))"
        >
          <option v-for="n in pageOptions" :key="n" :value="n">
            {{ n }} / página
          </option>
        </select>
      </div>

      <div class="d-flex align-items-center">
        <BPagination
          :model-value="pagination.page || 1"
          :per-page="pagination.limit || 10"
          :total-rows="pagination.total || 0"
          size="sm"
          limit="5"
          first-text="«"
          prev-text="‹"
          next-text="›"
          last-text="»"
          @update:model-value="emit('change-page', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { BPagination } from 'bootstrap-vue-next'

const props = defineProps({
  items: { type: Array, default: () => [] },
  pagination: { type: Object, default: () => ({}) },
  totalLabel: { type: String, default: '' }
})

const emit = defineEmits(['change-page', 'change-limit', 'detalhar', 'tratar'])

const pageOptions = [10, 15, 20, 25, 50]

const startRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return (page - 1) * limit + 1
})

const endRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return Math.min(page * limit, total)
})

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}

function normalizeStatus(v) {
  const map = {
    PENDENTE: 'Pendente',
    CONCILIADO_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_MANUAL: 'Conciliado manual',
    PENDENTE_AJUSTE: 'Pendente ajuste',
    IGNORADO: 'Ignorado',
    DIVERGENTE: 'Divergente',

    AGUARDANDO_CONCILIACAO_BANCO_OPERADORA: 'Aguardando conciliação Banco x Operadora',
    PAGAMENTO_CONSOLIDADO: 'Pagamento consolidado',
    DESPESA_BANCARIA: 'Despesa bancária',
    PENDENTE_SISTEMA: 'Pendente sistema',

    CONCILIADO_BANCO_OPERADORA_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_BANCO_OPERADORA_MANUAL: 'Conciliado manual',

    PIX_RECEBIDO: 'Pix recebido',
    PIX_ENVIADO: 'Pix enviado',
    CARTAO_GETNET_CREDITO: 'Getnet crédito',
    CARTAO_GETNET_DEBITO: 'Getnet débito',
    CHEQUE_DEPOSITO: 'Cheque depósito',
    CHEQUE_DEVOLVIDO: 'Cheque devolvido',
    BOLETO: 'Boleto',
    DARF: 'DARF',
    APLICACAO: 'Aplicação',
    RESGATE_APLICACAO: 'Resgate aplicação',
    SALARIOS: 'Salários',
    TARIFA_BANCARIA: 'Tarifa bancária',
    TRANSFERENCIA_BANCARIA: 'Transferência bancária',
    TRANSFERENCIA_INTERNA: 'Transferência interna',
    DEBITO_AUTOMATICO: 'Débito automático',
    OUTRO: 'Outro',

    SEM_LANCAMENTO_SISTEMA: 'Sem lançamento no sistema',
    AUTORIZACAO_DIVERGENTE: 'Autorização divergente',
    AMBIGUO: 'Ambíguo'
  }

  const key = String(v || '').toUpperCase()
  return map[key] || String(v || '').replaceAll('_', ' ')
}

function eventoLabel(v) {
  const s = String(v || '').toUpperCase()
  if (s === 'CHARGEBACK') return 'Chargeback'
  if (s === 'CANCELAMENTO') return 'Cancelamento'
  return s || 'Evento'
}

function eventoClass(v) {
  const s = String(v || '').toUpperCase()
  if (s === 'CHARGEBACK') return 'is-red'
  if (s === 'CANCELAMENTO') return 'is-gray'
  return 'is-slate'
}

function parcelaLabel(item) {
  if (!item?.parcela_numero) return '—'
  return `${item.parcela_numero}/${item.parcela_total || item.parcela_numero}`
}

function resumirTexto(v) {
  const texto = String(v || '').trim()
  if (!texto) return '—'
  if (texto.length <= 40) return texto
  return `${texto.slice(0, 40)}...`
}

function dataReferencia(item) {
  return item?.data_referencia || item?.data_vencimento || item?.data_venda || null
}

function valorPrincipal(item) {
  if (item?.valor_evento != null) return item.valor_evento
  if (item?.valor_bruto != null) return item.valor_bruto
  if (item?.valor_parcela != null) return item.valor_parcela
  if (item?.valor_liquido != null) return item.valor_liquido
  return 0
}

function isPendenteAjuste(item) {
  return String(item?.status_conciliacao || 'PENDENTE_AJUSTE').toUpperCase() === 'PENDENTE_AJUSTE'
}
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.table th {
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  font-size: 0.83rem;
  vertical-align: middle;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.status-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
}

.table-action-cell {
  text-align: right !important;
  vertical-align: middle !important;
  white-space: nowrap;
}

.action-icon {
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.15s ease;
}

.action-icon:hover {
  opacity: 0.75;
}

:deep(.pagination.pagination-sm .page-link) {
  font-size: 0.8rem;
  line-height: 1;
  border-radius: 0 !important;
}

:deep(.pagination.pagination-sm) {
  margin-bottom: 0;
}

:deep(.pagination.pagination-sm .page-item.active .page-link) {
  z-index: 1;
}

.is-amber { background: #fde68a; color: #92400e; }
.is-red { background: #fee2e2; color: #b91c1c; }
.is-gray { background: #e5e7eb; color: #374151; }
.is-slate { background: #e2e8f0; color: #334155; }

.diff-zero { color: #111827; }
.diff-fee { color: #b45309; font-weight: 600; }
.diff-alert { color: #b91c1c; font-weight: 700; }
</style>